import {useCallback, useEffect, useRef} from 'react'
import {useAppDispatch} from '../../state/state'

import {GenericActions} from '../../state/GenericSlice'
import {
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    DocumentData,
    getDocs,
    onSnapshot,
    QueryDocumentSnapshot,
    QuerySnapshot,
    setDoc,
    updateDoc
} from 'firebase/firestore'
import {db, storage} from "../../Config/firebase"
import {toast} from 'react-toastify'
import {CollectionOptions} from './types'
import {getQuery} from './getQuery'
import {getDownloadURL, ref, uploadBytes} from "firebase/storage";
import axios from "axios";

type ListnerState = {
    name?: string
    unsubscribe: () => void
}


export const useBackend = <T extends DocumentData>(path: string) => {
    const listenersRef = useRef<ListnerState[]>([]);
    const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);
    const hasMore = useRef(true);
    const ignore = useRef(false);

    useEffect(() => {
        let listenerRefValue: ListnerState[] | null = null;

        if (listenersRef.current) {
            listenerRefValue = listenersRef.current;
        }

        return () => {
            if (listenerRefValue) {
                listenerRefValue.forEach(listener => {
                    listener.unsubscribe();
                })
            }
        }
    }, [])

    const dispatch = useAppDispatch();

    const loadCollection = useCallback((actions: GenericActions<T>, options?: CollectionOptions, role?:any) => {

        if (options?.reset) {
            lastDocRef.current = null;
            hasMore.current = true;
            dispatch(actions.reset())
        }

        dispatch(actions.loading());

        const query = getQuery(path, options, lastDocRef);


        const processQuery = (querySnapshot: QuerySnapshot<DocumentData, DocumentData>) => {
            const data: DocumentData[] = [];
            if (querySnapshot.empty) {
                hasMore.current = false;
                dispatch(actions.success([] as unknown as T));
                return;
            }
            querySnapshot.forEach(doc => {
                data.push({id: doc.id, ...doc.data()})
            })
            if (options?.pagination && options.limit) {
                lastDocRef.current = querySnapshot.docs[querySnapshot.docs.length - 1];
                hasMore.current = !(querySnapshot.docs.length < options.limit);
            }

            dispatch(actions.success({data, role}))
        }

        if (options?.get) {
            getDocs(query)
                .then(querySnapshot => {
                    processQuery(querySnapshot);
                })
        } else {
            const listener = onSnapshot(query, {
                next: querySnapshot => {
                    processQuery(querySnapshot);
                },
                error: error => {
                    dispatch(actions.error(error.message));

                }
            })
            listenersRef.current.push({name: path, unsubscribe: listener});
        }
    }, [dispatch, path])

    const loadDocument = useCallback((id: string, actions: GenericActions<T>) => {
        dispatch(actions.loading());
        const docRef = doc(db, path, id);

        const listener = onSnapshot(docRef, {
            next: doc => {
                if (!doc.exists) {
                    dispatch(actions.error('Document does not exist'));
                    return;
                }
                dispatch(actions.success({id: doc.id, ...doc.data()} as unknown as T))
            }
        })
        listenersRef.current.push({name: path + '/' + id, unsubscribe: listener})
    }, [dispatch, path])

    const create = async (data: T) => {
        const ref = doc(collection(db, path));
        await setDoc(ref, data);
        return ref;
    }

    const update = async (id: string, data: T) => {
       
        const docRef = doc(db, path, id);
       
        try {
            return await updateDoc(docRef, data);

        } catch (error: any) {

            toast.error(error.message);
            return "error"
        }
    }

    const updateArrayField = async (id: string, fieldName: string, data: T) => {
        const docRef = doc(db, path, id);
        try {
            await updateDoc(docRef, {
                [fieldName]: arrayUnion(data)
            });
        } catch (error: any) {

            toast.error(error.message);
        }
    }

    const remove = async (id: string) => {
        try {
            return await deleteDoc(doc(db, path, id));
        } catch (error: any) {
            toast.error(error.message);
            return "error"
        }
    }

    const set = async (id: string, data: any) => {
        try {
            return await setDoc(doc(db, path, id), data);
        } catch (error: any) {

            toast.error(error.message)
        }
    }


    const setDocField = async (id: string, fieldName: string, data: T) => {
        const docRef = doc(db, path, id);
        try {
            await setDoc(docRef, {
                [fieldName]: data
            },{ merge: true });
        } catch (error: any) {

            toast.error(error.message);
        }
    }

    const fetchAPI = async (endpoint: string) => {
        try {
            return await fetch(endpoint)
        } catch (error: any) {

            toast.error(error.message);
            return null
        }
    }

    const axiosPostRequest = async (endpoint: string, body: any) => {
        try {
            const response = await axios.post(endpoint, body);
            const successResponse = {
                status: response.status, // Success status code
                message: "Request successful", // Success message
                data: response.data // The response data
            };
            return successResponse;
        } catch (error: any) {
            const errorResponse = {
                status: error.response?.status || "Unknown Status", // Error status code
                message: error.response?.data?.message || error.message || "An unknown error occurred" // Error message
            };

            // toast.error(`${errorResponse.status}: ${errorResponse.message}`);
            return errorResponse;
        }
    }

    const axiosGetRequest = async (endpoint: string,queryParams:any) => {
        try{
            return await axios.get(endpoint,{params:{...queryParams}})
        }catch(error: any){

            // toast.error(error.message);
            return "error";
        }
    }

    const uploadFileInFirestore = async (userId: string, file: File) => {
        try {
            const timeStamp = new Date().toLocaleTimeString();
            const storageRef = ref(storage, `users/${userId}/uploadsref/${timeStamp}`);
            await uploadBytes(storageRef, file);
            const profilePictureURL = await getDownloadURL(storageRef);
            // console.log(profilePictureURL)
            return profilePictureURL;
        } catch (error: any) {

            toast.error("Error uploading file please try again");
            return "error"
        }
    }


    return {
        loadCollection,
        loadDocument,
        create,
        update,
        remove,
        set,
        setDocField,
        hasMore,
        ignore,
        fetchAPI,
        updateArrayField,
        axiosPostRequest,
        axiosGetRequest,
        uploadFileInFirestore


    }
}